import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from './supabase'

const AuthCtx = createContext(null)

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  // Bug fix 2026-06-03: track si getSession() inicial ya respondió. Sin esto,
  // el primer render tenía session=null + loading=false antes de que Supabase
  // resolviera la sesión cacheada → RequireAuth navegaba a /login → /admin.
  // Síntoma: entrar fresh a /cliente/tienda terminaba en /admin para admins.
  const [sessionChecked, setSessionChecked] = useState(false)

  // Carga session inicial + escucha cambios
  useEffect(() => {
    let active = true

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return
      setSession(data.session)
      setSessionChecked(true)
    })

    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess)
      setSessionChecked(true)
    })

    return () => {
      active = false
      sub.subscription.unsubscribe()
    }
  }, [])

  // Cuando hay session, carga el profile (rol + nombre)
  useEffect(() => {
    if (!sessionChecked) return  // esperar a que getSession() haya respondido
    if (!session) {
      setProfile(null)
      setLoading(false)
      return
    }
    setLoading(true)
    supabase
      .from('profiles')
      .select('id, role, nombre, phone, email, direccion, apto, barrio_id, direccion_referencias, cuenta_baja_at')
      .eq('id', session.user.id)
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Error cargando profile:', error)
          setProfile(null)
        } else if (data?.cuenta_baja_at) {
          // Cuenta dada de baja → signOut automático y redirect a /login?baja=ok
          // Esto cubre el caso "se dio de baja en otra sesión / dispositivo"
          // o "alguien con cuenta cerrada vuelve a loguearse meses después".
          supabase.auth.signOut()
          setProfile(null)
          if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
            window.location.replace('/login?baja=ok')
          }
        } else {
          setProfile(data)
        }
        setLoading(false)
      })
  }, [session, sessionChecked])

  // Re-lee el profile desde la DB. Usarlo después de actualizarMiPerfil()
  // para que componentes como AvisoPerfilIncompleto vean los datos nuevos.
  async function refreshProfile() {
    if (!session?.user?.id) return null
    const { data, error } = await supabase
      .from('profiles')
      .select('id, role, nombre, phone, email, direccion, apto, barrio_id, direccion_referencias, cuenta_baja_at')
      .eq('id', session.user.id)
      .single()
    if (error) {
      console.error('Error refrescando profile:', error)
      return null
    }
    if (data?.cuenta_baja_at) {
      supabase.auth.signOut()
      setProfile(null)
      return null
    }
    setProfile(data)
    return data
  }

  const value = {
    session,
    user: session?.user ?? null,
    profile,
    role: profile?.role ?? null,
    // RBAC helpers — desde 2026-06-10 separamos admin (read-only) vs superadmin (write).
    // isSuperadmin: solo Nico + Roberto pueden modificar configuración crítica.
    // isAdminOrSuperadmin: ambos roles ven el panel admin completo.
    isSuperadmin: profile?.role === 'superadmin',
    isAdminOrSuperadmin: profile?.role === 'admin' || profile?.role === 'superadmin',
    loading,
    refreshProfile,
    // Fix seguridad 7-jul-2026: signOut más contundente. Sin esto, en PC pública
    // el próximo user podía entrar auto con "Continuar con Google" (sesión Google seguía activa).
    // Ahora: limpiamos sesión Supabase + storage + redirect con flag ?logout=1 para
    // mostrar banner "Recordá cerrar Google también si estás en PC pública".
    signOut: async () => {
      await supabase.auth.signOut()
      try {
        localStorage.clear()
        sessionStorage.clear()
      } catch {}
      if (typeof window !== 'undefined') {
        window.location.replace('/login?logout=1')
      }
    },
  }

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthCtx)
  if (!ctx) throw new Error('useAuth fuera de AuthProvider')
  return ctx
}
